import { useState } from "react";
import { toast } from "sonner";
import { MessageSquarePlus, Plus, X } from "lucide-react";
import { api } from "./api";

const AREAS = [["", "Anywhere on the website"], ["hero", "Main heading / introduction"], ["services", "Services"],
  ["about", "About"], ["process", "How it works"], ["why", "Why choose us"], ["service_area", "Where we serve"],
  ["faq", "FAQs"], ["cta", "Call to action"], ["other", "Contact / page title"]];
const blank = () => ({ area: "", request: "" });
const field = "rounded-xl bg-slate-950 ring-1 ring-slate-700 p-3 text-sm text-slate-100 focus:outline-none focus:ring-sky-500";

function Row({ row, i, onChange, onRemove, canRemove }) {
  return (
    <div className="rounded-xl ring-1 ring-slate-800 p-3 space-y-2" data-testid={`alias-change-row-${i}`}>
      <div className="flex gap-2">
        <select value={row.area} onChange={(e) => onChange({ ...row, area: e.target.value })}
          data-testid={`alias-change-area-${i}`} className={`${field} flex-1 py-2`}>
          {AREAS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {canRemove && (
          <button onClick={onRemove} data-testid={`alias-change-remove-${i}`} aria-label="Remove change"
            className="rounded-xl px-2 text-slate-400 hover:bg-slate-800/60 transition-colors"><X className="h-4 w-4" /></button>
        )}
      </div>
      <textarea value={row.request} rows={2} maxLength={2000} onChange={(e) => onChange({ ...row, request: e.target.value })}
        placeholder="e.g. Change the About heading to 'Beautiful cakes made with love'."
        data-testid={`alias-change-text-${i}`} className={`${field} w-full`} />
    </div>
  );
}

// TEST-043: Alias internal change request — plain English; does not use the client's revision.
export function RequestChanges({ jobId, version, onDone }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([blank()]);
  const [busy, setBusy] = useState(false);
  const items = rows.filter((r) => r.request.trim()).map((r) => ({ area: r.area || null, request: r.request.trim() }));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.changeRequest(jobId, version, items);
      toast.success(`${r.version} created: ${r.applied} applied, ${r.needsReview} need review, ${r.unsupported} not supported`);
      setOpen(false); setRows([blank()]); onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="alias-change-open"
        className="w-full flex items-center justify-center gap-2 rounded-2xl bg-sky-500/10 ring-1 ring-sky-500/40 py-3 text-sm font-semibold text-sky-200 hover:bg-sky-500/20 transition-colors">
        <MessageSquarePlus className="h-4 w-4" /> Request website changes
      </button>
    );
  }
  return (
    <section className="rounded-2xl bg-slate-900/70 ring-1 ring-sky-500/40 p-4 space-y-3" data-testid="alias-change-panel">
      <p className="font-medium">Request website changes to {version}</p>
      <p className="text-sm text-slate-400">Describe each change in plain English. This is an internal Alias change and does not use the client's revision. Every request is reported as applied, needing review, or not supported.</p>
      {rows.map((row, i) => (
        <Row key={i} row={row} i={i} canRemove={rows.length > 1}
          onChange={(v) => setRows(rows.map((r, j) => (j === i ? v : r)))}
          onRemove={() => setRows(rows.filter((_, j) => j !== i))} />
      ))}
      <button onClick={() => setRows([...rows, blank()])} disabled={rows.length >= 20} data-testid="alias-change-add"
        className="flex items-center gap-1 text-sm text-sky-300 hover:text-sky-200 transition-colors"><Plus className="h-4 w-4" /> Add another change</button>
      <div className="flex gap-2 justify-end">
        <button onClick={() => setOpen(false)} disabled={busy} data-testid="alias-change-cancel"
          className="rounded-xl px-4 py-2 text-sm text-slate-300 hover:bg-slate-800/60 transition-colors">Cancel</button>
        <button onClick={submit} disabled={busy || !items.length} data-testid="alias-change-submit"
          className="rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-sky-400 disabled:opacity-50 transition-colors">
          {busy ? "Creating…" : "Create updated version"}
        </button>
      </div>
    </section>
  );
}
